import './styles.scss';

import { Button, Form, InputGroup } from 'react-bootstrap';
import { FloatingPortal, autoUpdate, flip, offset, shift, useDismiss, useFloating, useInteractions, useRole } from '@floating-ui/react';
import { QueryInputType, getClasses, queryInput } from '@/utils';
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { LoadingBlur } from '@/components/LoadingSpinner';
import { SortDirectionEnum } from '@/models/gen/graphql';
import Tippy from '@tippyjs/react';
import { convertDtrLocationToPotentialLocation } from '@/features/DriveTimeTool/api/services';
import { formatFullAddress } from '@/features/DriveTimeTool/utils/locations';
import searchDtrLocation from '@/features/DriveTimeTool/api/services/searchDtrLocation';
import { useDriveTimeTool } from '@/features/DriveTimeTool';

export interface LocationOption {
  id: string;
  name: string;
  address: string;
}

interface LocationDropdownProps {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  className?: string;
  onAddLocationClick?: () => void;
  addButtonTooltip?: string;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  value,
  onChange,
  placeholder,
  className = '',
  onAddLocationClick,
  addButtonTooltip = 'Create new location',
}) => {
  const [state, setState] = useDriveTimeTool(({ state, setState }) => [
    {
      locations: state.locations,
      loading: state.loading.locations,
    },
    setState,
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [originalValue, setOriginalValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (open) => {
      if (open && value) {
        // Store original value and clear search text when opening
        setOriginalValue(value);
        setSearchText('');
      } else if (!open && searchText === '' && originalValue) {
        // Restore original value if user didn't search for anything
        // Value stays the same since no new selection was made
      } else if (!open && searchText && !originalValue) {
        // Clear search text if closing without selection
        setSearchText('');
      }
      setIsOpen(open);
    },
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  // dedeupe locations by address so they render once
  const locations = useMemo(() => {
    const output: LocationOption[] = [];
    const addressToLocationId: Record<string, boolean> = {};
    Array.from(state.locations.values()).forEach((loc) => {
      const address = formatFullAddress(convertDtrLocationToPotentialLocation(loc));
      if (addressToLocationId[address]) return;
      addressToLocationId[address] = true;
      output.push({
        id: loc.id,
        name: loc.name,
        address,
      });
    });
    return output;
  }, [state.locations]);

  const dismiss = useDismiss(context);
  const role = useRole(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  // Calculate the width to match the reference element
  const dropdownStyles = {
    ...floatingStyles,
    width: refs.reference.current ? (refs.reference.current as HTMLElement).offsetWidth : 'auto',
  };

  const getLocations = async () => {
    setState((current) => ({ ...current, loading: { ...current.loading, locations: true } }));
    const res = await searchDtrLocation([{ name: queryInput(null, QueryInputType.DEFAULT, SortDirectionEnum.Asc, 0) }], { pageSize: null });
    const rows = res?.rows || [];

    // group locations by address so we can can filter for duplicate locations
    const groupedLocationIdsByAddress = new Map<string, string[]>();
    const locations = new Map();
    rows.forEach((loc) => {
      // push location id to array
      const address = formatFullAddress(convertDtrLocationToPotentialLocation(loc));
      if (!groupedLocationIdsByAddress.has(address)) groupedLocationIdsByAddress.set(address, []);
      groupedLocationIdsByAddress.get(address).push(loc.id);
      // map location Id to location
      if (!locations.has(loc.id)) locations.set(loc.id, loc);
    });
    setState((current) => ({
      ...current,
      locations,
      groupedLocationIdsByAddress,
      loading: { ...current.loading, locations: false },
    }));
  };

  // get the locations before component mount
  useLayoutEffect(() => {
    getLocations();
  }, []);

  // refetch locations when component is opened
  useEffect(() => {
    if (!isOpen && locations.length > 0) return;
    getLocations();
  }, [isOpen]);

  const filteredOptions = useMemo(() => {
    if (!searchText) return locations;
    const lower = searchText.toLowerCase();
    return locations.filter((option) => option.name.toLowerCase().includes(lower) || option.address.toLowerCase().includes(lower));
  }, [searchText, locations]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSearchText = e.target.value;
    setSearchText(newSearchText);
    setIsOpen(true);
  };

  const handleOptionSelect = (option: LocationOption) => {
    const newValue = option.id === value ? null : option.id;
    onChange(newValue);
    setSearchText('');
    setOriginalValue('');
    setIsOpen(false);
    setTimeout(() => {
      inputRef.current?.blur();
    }, 0);
  };

  const handleSearchButtonClick = () => {
    inputRef.current?.focus();
    setIsOpen(true);
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };
  const handleClearClick = () => {
    onChange(null);
    setSearchText('');
    setOriginalValue('');
    setIsOpen(true);
    inputRef.current?.focus();
  };

  const displayName = useMemo(() => {
    const location = locations.find((loc) => loc.id === value);
    return location ? [location.name, location.address].filter(Boolean).join(', ') : '';
  }, [locations, value]);

  return (
    <div className={`location-dropdown ${className}`}>
      <div className="location-input-row" ref={refs.setReference} {...getReferenceProps()}>
        <InputGroup className="location-input-group">
          <Form.Control
            ref={inputRef}
            type="text"
            value={isOpen ? searchText : displayName}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            placeholder={isOpen && displayName ? displayName : placeholder}
            className="location-input"
          />
          <Button variant="icon" className="search-btn" onClick={!displayName ? handleSearchButtonClick : handleClearClick}>
            <i className={getClasses('fas fa-fw', !displayName ? 'fa-search' : 'fa-times')}></i>
          </Button>
        </InputGroup>
        {onAddLocationClick && (
          <Tippy content={addButtonTooltip} className="tippy-dark">
            <Button variant="icon" className="add-btn-separate" onClick={onAddLocationClick}>
              <i className="sv sv-plus-square"></i>
            </Button>
          </Tippy>
        )}
      </div>
      {isOpen && (
        <FloatingPortal>
          <div className="dropdown-menu-custom" ref={refs.setFloating} style={dropdownStyles} {...getFloatingProps()}>
            <LoadingBlur loading={state.loading} />
            <div className="dropdown-options">
              {!filteredOptions.length && (
                <div className="dropdown-option dropdown-empty" onClick={onAddLocationClick}>
                  <div className="option-content text-center">
                    <div className="option-text">
                      <div className="option-code text-muted">No Locations Found</div>
                      {onAddLocationClick && <div className="option-address text-muted">Click here to add a location.</div>}
                      {!onAddLocationClick && <div className="option-address text-muted">Add a location to select.</div>}
                    </div>
                  </div>
                </div>
              )}
              {filteredOptions
                .sort((a, b) => a.name.localeCompare(b.name))
                .map((option) => (
                  <div
                    key={option.id}
                    className={`dropdown-option${value === `${option.name}, ${option.address}` ? ' active' : ''}`}
                    onClick={() => handleOptionSelect(option)}
                  >
                    <div className="option-content">
                      <i className="fas fa-map-marker-alt location-pin-icon"></i>
                      <div className="option-text">
                        <div className="option-code">{option.name}</div>
                        <div className="option-address">{option.address}</div>
                      </div>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default LocationDropdown;
